import React, { useEffect, useMemo } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';

import WebsiteStepOne from '@components/Publisher/Websites/Create/WebsiteStepOne/WebsiteStepOne';
import WebsiteStepTwo from '@components/Publisher/Websites/Create/WebsiteStepTwo/WebsiteStepTwo';
import WebsiteStepThree from '@components/Publisher/Websites/Create/WebsiteStepThree/WebsiteStepThree';
import WebsiteStepFour from '@components/Publisher/Websites/Create/WebsiteStepFour/WebsiteStepFour';

import urls from '@constants/urls';
import { PublishersWebsiteDetailsType } from '@ts/publishers.types';
import { useQuery, useQueryClient } from 'react-query';
import { getPublisherWebsiteApi } from '@api/publishers.api';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';

function WebsitePages(): JSX.Element {
  const params = useParams();

  const websiteId = useMemo(() => {
    return Number(params.newWebsiteId) || Number(params.websiteId);
  }, [params.websiteId, params.newWebsiteId]);

  const queryClient = useQueryClient();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const queryKey = ['website-details', currentWorkspaceId, websiteId];
  const hasCachedWebsite = useMemo(() => {
    return (
      currentWorkspaceId > 0 &&
      websiteId > 0 &&
      queryClient.getQueryData<PublishersWebsiteDetailsType>(queryKey) != null
    );
  }, [currentWorkspaceId, websiteId, queryClient]);

  const { data: website, refetch: refetchWebsite } = useQuery<PublishersWebsiteDetailsType>(
    queryKey,
    () => getPublisherWebsiteApi(websiteId, currentWorkspaceId),
    {
      enabled: currentWorkspaceId > 0 && websiteId > 0 && !hasCachedWebsite,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const updateWebsite = (payload: PublishersWebsiteDetailsType) => {
    queryClient.setQueryData(
      ['website-details', currentWorkspaceId, payload.id],
      (_data: PublishersWebsiteDetailsType) => payload
    );
  };

  useEffect(() => {
    if (websiteId > 0 && currentWorkspaceId > 0 && website == null && !hasCachedWebsite) {
      refetchWebsite();
    }
  }, [currentWorkspaceId, websiteId, hasCachedWebsite]);

  return (
    <Switch>
      <Route exact path={urls.WEBSITE_STEP_ONE()}>
        <WebsiteStepOne website={website} updateWebsite={updateWebsite} />
      </Route>
      <Route exact path={urls.WEBSITE_STEP_TWO()}>
        <WebsiteStepTwo website={website} updateWebsite={updateWebsite} />
      </Route>
      <Route exact path={urls.WEBSITE_STEP_THREE()}>
        <WebsiteStepThree website={website} updateWebsite={updateWebsite} />
      </Route>
      <Route exact path={urls.WEBSITE_STEP_FOUR()}>
        <WebsiteStepFour website={website} updateWebsite={updateWebsite} />
      </Route>
    </Switch>
  );
}

export default WebsitePages;
